import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import translationFR from './locales/fr/translation.json';
import translationEN from './locales/en/translation.json';
import { DateHelper, LocaleHelper, LocaleManager } from '@bryntum/core-thin';
import { configureSyncfusionI18n } from './utils/syncfusionI18n';
import { constraintTypePickerLocale } from './utils/bryntumConstraintLabels';
import { readCachedTermOverrides } from './services/term-customization-services/applyTermOverrides';
import { buildTermNamespace } from './services/term-customization-services/termNamespace';

const LOCALE_MAP = {
    fr: { 
      bryntum: "FrFr", 
      date: "fr-FR", 
      import: async () => {
        // Core localisations
        await import('@bryntum/core-thin/lib/localization/FrFr.js');
        // Engine localisations
        await import('@bryntum/engine-thin/lib/localization/FrFr.js');
        // Grid localisations
        await import('@bryntum/grid-thin/lib/localization/FrFr.js');
        // Scheduler localisations
        await import('@bryntum/scheduler-thin/lib/localization/FrFr.js');
        // SchedulerPro localisations
        await import('@bryntum/schedulerpro-thin/lib/localization/FrFr.js');
        // Gantt localisations
        await import('@bryntum/gantt-thin/lib/localization/FrFr.js');
        // Calendar localisations
        await import('@bryntum/calendar-thin/lib/localization/FrFr.js');
        // TaskBoard localisations
        await import('@bryntum/taskboard-thin/lib/localization/FrFr.js');
      }
    },
    en: { 
      bryntum: "En", 
      date: "en-GB", 
      import: async () => {
        await import('@bryntum/core-thin/lib/localization/En.js');
        await import('@bryntum/engine-thin/lib/localization/En.js');
        await import('@bryntum/grid-thin/lib/localization/En.js');
        await import('@bryntum/scheduler-thin/lib/localization/En.js');
        await import('@bryntum/schedulerpro-thin/lib/localization/En.js');
        await import('@bryntum/gantt-thin/lib/localization/En.js');
        await import('@bryntum/calendar-thin/lib/localization/En.js');
        await import('@bryntum/taskboard-thin/lib/localization/En.js');
      }
    }
};

async function applyBryntumLocale(i18nLang) {
  const lang = i18nLang.startsWith("fr") ? "fr" : "en";
  const { bryntum, date, import: importLocale } = LOCALE_MAP[lang];

  await importLocale();
  // The Constraint type cell follows the wording of our translation file (SPEC-0059 §5.9),
  // merged into Bryntum's locale before it is applied.
  LocaleHelper.publishLocale(bryntum, {
    ConstraintTypePicker: constraintTypePickerLocale(lang === "fr" ? translationFR : translationEN),
  });

  LocaleHelper.localeName = bryntum;
  LocaleManager.applyLocale(bryntum);
  DateHelper.locale = date;
  DateHelper.defaultFormat = bryntum === "FrFr" ? "DD/MM/YYYY" : "MM/DD/YYYY";
  // Monday-first week start (ISO 8601) is enforced per-component via
  // the `weekStartDay={GANTT_WEEK_START_DAY}` prop on every BryntumGantt
  // (see Gantt/utils.js). Earlier we tried setting it once here via
  // `DateHelper.weekStartDay = 1`, but Bryntum 6+ exposes
  // `DateHelper.weekStartDay` as a getter-only static — assigning to it
  // throws TypeError and breaks i18n init.

    // Custom translations for Bryntum components not yet supported in FrFr
    if (bryntum === "FrFr" && window.bryntum && window.bryntum.locales) {
  window.bryntum.locales.FrFr.TaskBoard = window.bryntum.locales.FrFr.TaskBoard || {};
  window.bryntum.locales.FrFr.TaskBoard.addTask = "Ajouter une tâche";
  window.bryntum.locales.FrFr.TaskBoard.collapse = "Réduire";
  window.bryntum.locales.FrFr.TaskBoard.expand = "Développer";
  window.bryntum.locales.FrFr.TaskBoard.removeTask = "Supprimer une tâche";
  window.bryntum.locales.FrFr.TaskBoard.removeTasks = "Supprimer les tâches";


  }
};

// Apply any cached term overrides BEFORE init so module-level `i18n.t('term.*')`
// (Bryntum/Syncfusion configs) returns customer wording on first evaluation.
const cachedTermOverrides = readCachedTermOverrides() || {};
const resources = {
  fr: { translation: { ...translationFR, term: buildTermNamespace('fr', cachedTermOverrides) } },
  en: { translation: { ...translationEN, term: buildTermNamespace('en', cachedTermOverrides) } }
};

const savedLang = localStorage.getItem('i18n-lang') || 'en';
applyBryntumLocale(savedLang);
configureSyncfusionI18n(savedLang);

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: savedLang,
    defaultValue: '_NOT_TRANSLATED_',
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    parseMissingKeyHandler: (key) => {
      const parts = key.split(".");
      return parts[parts.length - 1];
    }
  });

i18n.on('languageChanged', (lng) => {
  localStorage.setItem('i18n-lang', lng);
  applyBryntumLocale(lng);
    if (lng === "fr") {
    DateHelper.locale = "fr-FR";
    DateHelper.defaultFormat = "DD/MM/YYYY";
  } else {
    DateHelper.locale = "en-US";
    DateHelper.defaultFormat = "MM/DD/YYYY";
  }
  configureSyncfusionI18n(lng);
});

// Exposed for end-to-end automation only: the in-app language switch (Account
// page) opens a new tab, so a spec has no other way to flip the language while
// a Gantt is mounted — the transition that rebuilds the Bryntum instance.
window.i18n = i18n;

export default i18n;
